import { useState, useEffect, useRef, Fragment } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import boost from 'highcharts/modules/boost';
import clsx from 'clsx';
import _ from 'lodash';
// import jsPDF from 'jspdf';
// import domtoimage from 'dom-to-image';
import { PDFExport } from "@progress/kendo-react-pdf";


//Redux
import { StateModel, actions } from 'store/store';
import { useSelector, useDispatch } from 'react-redux';

//Components
import Loader, { LoaderTypes } from 'components/loader/Loader';
import Input from 'components/input/Input';
import Nav from 'components/nav/Nav';
import Tab from 'components/tab/Tab';
import Button from 'components/button/Button';
import Checkbox from 'components/checkbox/Checkbox';
import { Table } from 'rsuite';
import Modal from 'components/modal/Modal';
import { faDownload, faEllipsisV, faExclamationTriangle, faInfoCircle } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

//Helpers
import { ChartLabelsPlotlines, ChartStats, ChartPlotbands, ConsumptionStats, CostStats, DuosStats } from 'store/capacity/capacityStateModel';
import { useOutsideClick } from 'shared/shared.hooks';
import { numberFormatter, consumptionTableHeaders, costTableHeaders, bandHeaders, metaMpan, snippets, chart, costGroups, pdfSuffix, duosTableHeaders } from './capacity.utilities';
import { plotlinesTooltips } from 'shared/shared.const';



export interface CapacityProps {
    companyName?: string;
    customerName?: string;
    email?: string;
    phoneNumber?: string;
    mpan?: string;
    public?: boolean;
    clearForm?: () => void;
    getCapacity?: (preCovid: boolean) => void;
}


boost(Highcharts);
const Capacity = (props: CapacityProps) => {
    const dispatch = useDispatch();
    const ddRef = useRef();
    const pdfExportComponent = useRef(null);
    const container = useRef(null);

    const [step, setStep] = useState(1);
    const [lines, setLines] = useState<string[]>([]);
    const [plotLines, setPlotLines] = useState<ChartLabelsPlotlines[]>([]);
    const [plotBands, setPlotBands] = useState<ChartPlotbands[]>([]);
    const [plotLinesX, setPlotLinesX] = useState([]);
    const [plotLinesY, setPlotLinesY] = useState([]);
    const [usagePlotlines, setUsagePlotlines] = useState([]);
    const [usagePlotbands, setUsagePlotbands] = useState([]);
    const [activeTab,] = useState("capacity");
    const [activeCovidTab, setActiveCovidTab] = useState("post-covid");
    const [openDd, setOpenDd] = useState(false);
    const [stats, setStats] = useState<ChartStats[]>([]);
    const [consumptionStats, setConsumptionStats] = useState<ConsumptionStats[]>([]);
    const [costStats, setCostStats] = useState<CostStats[]>([]);
    const [duosStats, setDuosStats] = useState<DuosStats[]>([]);
    const [immutable, setImmutable] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [bandsTableData, setBandsTableData] = useState([]);
    const [tooltipText, setTooltipText] = useState("");
    const [tooltipColor, setTooltipColor] = useState("");

    const [mpan, setMpan] = useState("");
    const [mpanTopline, setMpanTopline] = useState("");
    const [importCapacity, setImportCapacity] = useState(null);
    const [newMeterPrompt, setNewMeterPrompt] = useState(false);
    const [newMeterModal, setNewMeterModal] = useState(false);
    const [checkNewMeter, setCheckNewMeter] = useState(false);
    const [disabled, setDisabled] = useState(true);
    const [consent, setConsent] = useState(false);

    const { HeaderCell, Column, Cell, ColumnGroup } = Table;

    //Selectors
    const loading = useSelector((state: StateModel) => state.capacityState.fetchingCapacity);
    const capacity = useSelector((state: StateModel) => state.capacityState.capacity);
    const errorMessage = useSelector((state: StateModel) => state.capacityState.capacityErrorMessage);


    const closeDd = () => {
        setOpenDd(false);
    };
    useOutsideClick(ddRef, closeDd);

    const exportPDFWithComponent = () => {
        if (pdfExportComponent.current) {
          pdfExportComponent.current.save(() => {
            if (props.public) {
                setNewMeterPrompt(true);
            }
          });
        }
    };

    const getPublicCapacity = (covidStatus?: boolean) => {
        dispatch(actions.capacity.getPublicCapacityAsync(
            mpan,
            mpanTopline,
            importCapacity,
            props.phoneNumber,
            props.companyName,
            props.customerName,
            props.email,
            covidStatus
        ));
    }

    const next = (currentStep: number) => {
        if (currentStep === 3) {
            let status = {
                "post-covid": false,
                "pre-covid": true
            }

            getPublicCapacity(status[activeCovidTab]);
            setNewMeterModal(false);
            setStep(1);
            setCheckNewMeter(true);
        } else {
            setStep(currentStep);
        }
    }

    // const exportAsPdf = async () => {
    //     setDownloading(true);

    //     const pdfSuffix = {
    //         "pre-covid": "PRE_COVID",
    //         "post-covid": "POST_COVID"
    //     }

    //     const page = document.getElementById('root');
    //     const pdf = new jsPDF();
    //     if (pdf) {
    //         domtoimage.toPng(page)
    //         .then(imgData => {
    //             const imgProps= pdf.getImageProperties(imgData);
    //             const pdfWidth = pdf.internal.pageSize.getWidth();
    //             const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

    //             // pdf.addImage(imgData, 'PNG', -35, 0, 280, 297);
    //             pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    //             pdf.save(`${props.mpan}_TCR_ANALYSIS_${pdfSuffix[activeCovidTab]}.pdf`);

    //             setDownloading(false);
    //         });
    //     }
    // }


    const NumberCell = ({ rowData, dataKey, className, ...props }) => (
        <Cell className={className} {...props}>{numberFormatter(rowData[dataKey])}</Cell>
    );

    const options = {
        chart: {
            zoomType: 'x',
            height: 750
        },
    
        boost: {
            useGPUTranslations: true
        },

        title: {
            text: ''
        },

        loading: {
            showDuration: 1000
        },

        credits: {
            enabled: false
        },
    
        tooltip: {
            valueDecimals: 2,
            backgroundColor: '#fff',
            shadow: false,
            shared: true,
            useHTML: true,
            shape: 'rect',
            positioner: function () {
                return { x: 105, y: 20 };
            }
        },
        plotOptions: {
            column: {
                borderRadiusTopLeft: 0,
                borderRadiusTopRight: 0,
                borderWidth: 0
            }
        },
        xAxis: chart(capacity, plotLinesX, plotLinesY, usagePlotlines, usagePlotbands).xAxis[activeTab],
        yAxis: chart(capacity, plotLinesX, plotLinesY, usagePlotlines, usagePlotbands).yAxis[activeTab],
        series: chart(capacity, plotLinesX, plotLinesY, usagePlotlines, usagePlotbands).series[activeTab]
    }

    const toggleLines = (lineName: string) => {
        if (lines.includes(lineName)) {
            setLines(lines.filter(line => line !== lineName));
        } else {
            setLines(prev => [...lines, lineName]);
        }
    }

    useEffect(() => {
        dispatch(actions.quotes.buildQuotesSuccess([]));
    }, [dispatch]);

    useEffect(() => {
        if (capacity) {
            let pX = [
                {
                    color: null,
                    dashStyle: 'shortdash',
                    value: capacity?.importCapacity, 
                    zIndex: 5,
                    width: 2,
                    label: { 
                        text: `Import Capacity => ${capacity?.importCapacity} kVA`,
                        verticalAlign: 'middle',
                        style: {
                            color: null,
                            fontWeight: "bold",
                            fontSize: '10px'
                        }
                    }
                }
            ];

            let pY = [];
            let uP = [];
            let uB = [];
            let rawLines: Record<string, ChartLabelsPlotlines> = {};
            let rawBands: Record<string, ChartPlotbands> = {};

            plotLines.forEach(plotLine => {
                rawLines = {
                    ...rawLines,
                    [plotLine.label]: plotLine
                }
            });

            plotBands.forEach(plotBand => {
                rawBands = {
                    ...rawBands,
                    [plotBand.label]: plotBand
                }
            });

            lines.forEach(line => {
                for (const [i, [key, value]] of Object.entries(Object.entries(rawLines))) {
                    
                    if (line === key && value.type === "x") {
                        const colors = ["#546de5", "#f5cd79", "#3dc1d3", "#f78fb3"]

                        pX.push({
                            color: colors[i],
                            dashStyle: 'longdashdot',
                            value: value.value, 
                            zIndex: 5,
                            width: 2,
                            label: { 
                                text: value.label,
                                verticalAlign: 'top',
                                style: {
                                    color: colors[i],
                                    fontWeight: "bold",
                                    fontSize: '10px'
                                }
                            }
                        }) 
                    } else if (line === key && value.type === "y") {
                        pY.push({
                            color: null,
                            dashStyle: 'shortdash',
                            value: value.value, 
                            zIndex: 5,
                            width: 2,
                            label: { 
                                text: value.label,
                                align: 'center',
                                style: {
                                    color: null,
                                    fontWeight: "bold",
                                    fontSize: '10px'
                                }
                            }
                        })
                    } else if (line === key) {
                        const colors = ["#f78fb3", "#574b90", "#f19066", "#546de5", "#f5cd79", "#3dc1d3"];
                        const spacing = [-50, -300, -580];

                        uP.push({
                            className: "my-plotline",
                            color: colors[i],
                            value: value.value, 
                            zIndex: 10,
                            width: 3,
                            tooltipText: plotlinesTooltips[i],
                            dashStyle: 'shortdashdot',
                            label: {
                                useHTML: true,
                                formatter: function (e) {
                                    return `<div class="pl">
                                                <div class="pl-label" style="background-color: ${colors[i]}">${value.label}</div>
                                            </div>`;
                                },
                                align: 'right',
                                x: spacing[i],
                                y: -10,
                                style: {
                                    color: "#fff",
                                    fontWeight: "bold",
                                    fontSize: '13px'
                                }
                            },
                            events: {
                                mouseover: function (e) {
                                    setImmutable(false);

                                    setTimeout(() => {
                                        setTooltipColor(colors[i]);
                                        setTooltipText(this.options?.tooltipText);
                                    }, 500);
                                },
                                mouseout: function (e) {
                                    setTooltipColor("");
                                    setTooltipText("");
                                }
                            }
                        })
                    }
                }

                for (const [i, [key, value]] of Object.entries(Object.entries(rawBands))) {
                    if (line === key) {
                        const colors = (opacity: number) => [`rgba(84, 109, 229, ${opacity})`, `rgba(61, 193, 211, ${opacity})`, `rgba(245, 205, 121, ${opacity})`, `rgba(255, 71, 87, ${opacity})`];

                        uB.push({
                            color: colors(.1)[i],
                            from: value.from,
                            to: value.to ? value.to : capacity.importCapacity * 100,
                            zIndex: 5,
                            label: {
                                useHTML: true,
                                formatter: function () {
                                    return `<div style="background-color: ${colors(.7)[i]}; padding: 5px; border-radius: 3px; margin-top: -10px">${value.label}</div>`;
                                },
                                align: 'bottom',
                                verticalAlign: 'bottom',
                                y: -5,
                                style: {
                                    color: "#fff",
                                    fontWeight: "bold",
                                    ontSize: '13px'
                                }
                            }
                        })
                    }
                }
            })

            setPlotLinesX(pX);
            setPlotLinesY(pY);
            setUsagePlotlines(uP);
            setUsagePlotbands(uB);
        }
    }, [lines, capacity, plotLines, plotBands]);

    useEffect(() => {
        if (capacity) {
            setStats([capacity.stats]);
            setConsumptionStats([capacity.consumptionStats]);
            setCostStats([capacity.costStats]);
            setDuosStats(capacity.duosStats);

            let tableBands = capacity.usageChart.plotbands.map((band, i) => {
                let labels = ["Band 1", "Band 2", "Band 3", "Band 4"];

                return {
                    band: labels[i],
                    lower: band.from > 1 ? band.from + 1 : band.from,
                    upper: i === 3 ? '∞' : band.to
                }
            })

            let pL = capacity.usageChart.annotations.map(line => line.label);
            let pB = capacity.usageChart.plotbands.map(band => band.label);

            setLines([...pL, ...pB]);
            setPlotLines(capacity.usageChart.annotations);
            setPlotBands(capacity.usageChart.plotbands);

            setBandsTableData(tableBands);
        }
    }, [capacity, activeTab]);

    useEffect(() => {
        if (step === 1) {
            setDisabled(!importCapacity || !mpanTopline || mpanTopline.length < 8 || !mpan || mpan.length < 13);
        } else if (step === 2) {
            setDisabled(!consent);
        }
    }, [step, mpan, mpanTopline, importCapacity, consent]);

    return (
        <PDFExport 
            ref={pdfExportComponent} 
            paperSize="auto" 
            margin={20} 
            fileName={`${props.mpan}_TCR_ANALYSIS_${pdfSuffix[activeCovidTab]}.pdf`} 
            author="NGP"
        >
            <Nav public={props.public} />
            <div className="chart" ref={container}>
                <Modal 
                    open={openModal}
                    component={
                        <div className="modal-content">
                            <div className="info">
                                <span className="info-title">TCR Changes</span>
                                <p>
                                    Your new DUoS cost, after the Targeted Charging Review (TCR), from April 2022 will be <span>£{numberFormatter(capacity?.costStats.newTotalCost)}</span>.
                                    This is {capacity?.costStats.totalCostDiff >= 0 ? 'an increase' : 'a decrease'} of <span>£{numberFormatter(capacity?.costStats.totalCostDiff)} ({capacity?.costStats.totalCostDiff > 0 ? '+' : ''}{capacity?.costStats.totalCostPercentChange}%)</span> from your current annual cost of <span>£{numberFormatter(capacity?.costStats.prevTotalCost)}</span>. 
                                </p>
                            </div>

                            <div className="info">
                                <span className="info-title">Capacity Recommendations</span>
                            </div>

                            { (capacity?.consumptionStats.maxDemand < capacity?.importCapacity ) && (
                                <div className="notice">
                                    <FontAwesomeIcon icon={faInfoCircle} className="icon" />
                                    <p>You reached a maximum demand of <span>{capacity.consumptionStats.maxDemand} kVA</span> on <span>{capacity.consumptionStats.maxDemandTime}</span>. Your allowed import capacity is <span>{capacity.importCapacity} kVA</span></p>
                                </div>                
                            ) }

                            { (capacity?.recommendation.action === null && capacity?.stats.maxApparentPower <= 0.75 * capacity?.importCapacity) && (
                                <div className="info">
                                    <p>
                                        Based on your usage data, your meter is already set at the optimum import capacity.
                                    </p>
                                </div>               
                            ) }

                            { (capacity?.recommendation.action === null && capacity?.stats.maxApparentPower > 0.75 * capacity?.importCapacity && capacity?.stats.maxApparentPower <= capacity?.importCapacity) && (
                                <div className="info">
                                    <p>
                                        Based on your usage data, you did not exceed your import capacity but came within 25% of doing so.
                                    </p>
                                    <p>
                                        We do not recommend taking any further action at this time, but you should you continue to monitor your usage.
                                    </p>
                                </div>               
                            ) }


                            { (capacity?.recommendation.action === 'reduce' && capacity?.costStats.totalCostDiff < 0) && (
                                <div className="info">
                                    <p>
                                        As you have capacity headroom, we recommend decreasing your import capacity to <span>{numberFormatter(capacity?.optimumCapacity.capacity)} kVA</span> to get the best possible price. 
                                    </p>
                                    <p>
                                        By reducing your capacity, your 2022 DUoS costs will be <span>£{numberFormatter(capacity?.costStats.newTotalCost - capacity?.costStats.newFixedCost + capacity?.optimumCapacity.cost)}</span>. This is an additional <span>£{numberFormatter(capacity?.costStats.newFixedCost - capacity?.optimumCapacity.cost)}</span> cheaper than if you were to take no action.
                                    </p>
                                </div>               
                            ) }

                            { (capacity?.recommendation.action === 'reduce' && capacity?.costStats.totalCostDiff > 0 && capacity?.optimumCapacity.costSaving > 0) && (
                                <div className="info">
                                    <p>
                                        As you have capacity headroom, we recommend decreasing your import capacity to <span>{numberFormatter(capacity?.optimumCapacity.capacity)} kVA</span> to get the best possible price. 
                                    </p>
                                    <p>
                                        By reducing your capacity, your 2022 DUoS costs will be <span>£{numberFormatter(capacity?.costStats.newTotalCost - capacity?.costStats.newFixedCost + capacity?.optimumCapacity.cost)}</span>. This is <span>£{numberFormatter(capacity?.costStats.newFixedCost - capacity?.optimumCapacity.cost)}</span> cheaper than if you were to take no action.
                                    </p>
                                </div>               
                            ) }

                            { (capacity.consumptionStats.maxDemand > capacity.importCapacity) && (
                                <div className="alert">
                                    <FontAwesomeIcon icon={faExclamationTriangle} className="icon" />
                                    <p>You reached a maximum demand of <span>{capacity.consumptionStats.maxDemand} kVA</span> on <span>{capacity.consumptionStats.maxDemandTime}</span>. Your allowed import capacity is <span>{capacity.importCapacity} kVA</span></p>
                                </div>                
                            ) }

                            { (capacity.consumptionStats.excessOccurrences > 1) && (
                                <div className="info">
                                    <p>
                                        As you have exceeded your import capacity multiple times, we recommend increasing your capacity to <span>{numberFormatter(capacity?.optimumCapacity.capacity)} kVA</span> to get the best possible price.
                                    </p>
                                    <p>
                                        By increasing your capacity, your 2022 DUoS costs will be <span>£{numberFormatter(capacity?.costStats.newTotalCost - capacity?.costStats.newFixedCost + capacity?.optimumCapacity.cost)}</span>. This is <span>£{numberFormatter(capacity?.costStats.newFixedCost - capacity?.optimumCapacity.cost)}</span> cheaper than if you were to take no action.
                                    </p>
                                </div>               
                            ) }

                            { (capacity.consumptionStats.excessOccurrences === 1) && (
                                <div className="info">
                                    <p>
                                        As you have only exceeded your import capacity once, we don't recommend making any changes.
                                    </p>
                                    <p>
                                        You should continually monitor your usage going forward, and if you continue to exceed your import capacity, then we recommend increasing your capacity to <span>{numberFormatter(capacity?.optimumCapacity.capacity)} kVA</span> to get the best possible price.
                                    </p>
                                    {/* <p>
                                        By increasing your capacity, your 2022 DUoS costs will be <span>£{numberFormatter(capacity?.costStats.newTotalCost - capacity?.costStats.newFixedCost + capacity?.optimumCapacity.cost)}</span>. This is <span>£{numberFormatter(capacity?.costStats.newFixedCost - capacity?.optimumCapacity.cost)}</span> cheaper than if you were to take no action.
                                    </p> */}
                                </div>               
                            ) }

                            <div className="warning">
                                <FontAwesomeIcon icon={faExclamationTriangle} className="icon" />
                                <span>Changing Import Capacity needs to be carefully considered.</span>
                            </div>

                            <div className="pros-cons">
                                <div className="group">
                                    <span className="title">Pros</span>
                                    <ul className="list">
                                        <li>Ensuring that your meter has the right capacity ensures that you pay the best possible price</li>
                                        <li>Under the TCR your import capacity determines your 'residual band' which has a very significant effect on the price you pay</li>
                                        <li>Better for all users of the distribution network as capacity is allocated where it is needed</li>
                                    </ul>
                                </div>
                                <div className="group">
                                    <span className="title">Cons</span>
                                    <ul className="list">
                                        <li>Whilst easy to decrease, a capacity increase is a lengthy process and at the discretion of your DNO – who may not have the extra capacity to allocate to you</li>
                                        <li>You need to consider future growth plans of your business and ensure you have capacity headroom for expansion</li>
                                        <li>Changes can only be requested once in any 12 month rolling period, so timing is crucial</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    }
                    closeModal={() => setOpenModal(false)}
                />
                <Modal 
                    open={newMeterModal}
                    component={
                        <div className="onboard-modal-content">
                            <div className="dots">
                                {[1,2].map((dot, index) => (
                                    <div className={clsx("dot", index + 1 === step && "active")} key={index}></div>
                                ))}
                            </div>

                            {step === 1 && (
                                <Fragment>
                                    <span className="title">Meter details</span>
                                    <Input 
                                        inputLabel="Meter Number"
                                        maxLength={13}
                                        inputValue={mpan}
                                        onChange={(e) => {
                                            setMpan(e.target.value);
                                        }}
                                    />
                                    <Input 
                                        inputLabel="Current Topline"
                                        maxLength={8}
                                        inputValue={mpanTopline}
                                        onChange={(e) => {
                                            setMpanTopline(e.target.value);
                                        }}
                                    />
                                    <Input 
                                        isNumber
                                        inputLabel="Import Capacity (kVA)"
                                        inputValue={importCapacity}
                                        onChange={(e) => setImportCapacity(e.target.value)}
                                    />
                                </Fragment>
                            )}

                            {step === 2 && (
                                <Fragment>
                                    <span className="title">Authorisation</span>
                                    <p>In order to analyse your usage, we must gather your industry-held metering and consumption data.</p>
                                    <p>By agreeing to proceed, you are confirming that you, <span>{props.customerName}</span>, on behalf of <span>{props.companyName}</span>, are authorising Northern Gas and Power to access your industry held metering and consumption data for the meter number <span>{mpan}</span>.</p>
                                    <p style={{ marginTop: "40px" }}>From time to time, we may wish to contact you with marketing information and updates about our products and services by email or telephone.</p>
                                    <Checkbox 
                                        text="By agreeing to proceed, I consent to receiving marketing information from Northern Gas and Power."
                                        isActive={consent}
                                        toggleCheckbox={() => setConsent(!consent)}
                                    />
                                </Fragment>
                            )}

                            {!_.isEmpty(errorMessage) && (
                                <div className="error">
                                    {errorMessage}
                                </div>
                            )}

                            <div className="actions">
                                <Button 
                                    text="previous"
                                    disabled={step === 1}
                                    onClick={() => setStep(step - 1)} 
                                />
                                <Button 
                                    text={step === 2 ? "submit" : "next"}
                                    isLoading={step === 2 && loading}
                                    loadingText="fetching-analysis"
                                    disabled={disabled}
                                    onClick={() => next(step + 1)} 
                                />
                            </div>
                        </div>
                    }
                    closeModal={() => {
                        setNewMeterModal(false);
                        setStep(1);
                    }}
                />
                <div className="name"><span>{props.companyName}</span></div>
                <div className="meta">
                    <Loader 
                        type={LoaderTypes.Big}
                        isLoading={loading && checkNewMeter}
                        loadingText="loading"
                    />

                    <div className="meta-data">
                        <div className="mpan-breakdown">
                            <span className="item-1">S</span>
                            {metaMpan(capacity?.mpan, capacity?.newTopline).map((mpb, index) => {
                                return (
                                    <span className="item-2" key={index}>{mpb}</span>
                                )
                            })}
                        </div>
                        <div className="stats">
                            {snippets(capacity).map((s, index) => (
                                <div className="snippet" key={index}>
                                    <div className="snippet_name">
                                        <FontAwesomeIcon icon={s.icon} className="snippet-icon" />
                                        {s.name}
                                    </div>
                                    <div className="snippet_info">{s.text}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="covid-status">
                    <Tab 
                        tabNames={[ "post-covid", "pre-covid" ]}
                        activeTab={activeCovidTab}
                        onClick={(n, i) => {
                            setActiveCovidTab(n);
                            setImmutable(true);
                            setCheckNewMeter(false);

                            let status = {
                                "post-covid": false,
                                "pre-covid": true
                            }

                            if (props.public && newMeterPrompt) {
                                getPublicCapacity(status[n]);
                            } else {
                                props.getCapacity(status[n]);
                            }
                        }}
                    />
                </div>

                <div className="export">
                    <div className="recommendation">
                        <span className="message">We recommend that you <span>{capacity?.recommendation.action ? capacity?.recommendation.action : 'keeping'}</span> your import capacity to <span>{numberFormatter(capacity?.optimumCapacity.capacity)}</span> kVA</span>
                        <span className="action" onClick={() => setOpenModal(true)}>View Details</span>
                    </div>
                    <div className="export-actions">
                        <Button 
                            showIcon
                            icon={faDownload}
                            text="Download" 
                            type="sec"
                            onClick={exportPDFWithComponent} 
                        />
                        {props.public && (
                            <Button 
                                text="Check another meter" 
                                type="sec"
                                onClick={() => setNewMeterModal(true)} 
                            />
                        )}
                        <Button 
                            text="Back" 
                            onClick={() => {
                                dispatch(actions.capacity.getCapacitySuccess(null));
                                props.clearForm();
                            }} 
                        />
                    </div>
                </div>

                <div className="table">
                    <div className="table-title">Consumption <div className="date">From <span>{capacity?.dataPeriod.from}</span> to <span>{capacity?.dataPeriod.to}</span> <span className="duration">({capacity?.dataPeriod.duration.months} months)</span></div></div>
                    <Table
                        height={117}
                        headerHeight={70}
                        bordered
                        cellBordered
                        loading={loading}
                        data={consumptionStats}
                    >
                        {consumptionTableHeaders.map((header, index) => (
                            <Column key={index} width={214}>
                                <HeaderCell>{header.headerName}</HeaderCell>
                                <NumberCell className="" rowData={consumptionStats} dataKey={header.field} />
                            </Column>
                        ))}
                    </Table>
                </div>

                <div className="table cost-breakdown">
                    <span className="table-title">Cost Breakdown</span>
                    <Table
                        height={176}
                        bordered
                        cellBordered
                        data={costStats}
                        headerHeight={120}
                        loading={loading}
                    >
                        {_.chunk(costTableHeaders, 4).map((col, i) => (
                            <ColumnGroup header={costGroups[i]} key={i}>
                                {col.map((header, index) => (
                                    <Column key={index}>
                                        <HeaderCell>{header.headerName}</HeaderCell>
                                        <NumberCell className="" rowData={costStats} dataKey={header.field} />
                                    </Column>
                                ))}
                            </ColumnGroup>
                        ))}
                    </Table>
                </div>

                <div className="table duos-breakdown">
                    <span className="table-title">DUoS Breakdown</span>
                    <Table
                        height={394}
                        bordered
                        cellBordered
                        data={duosStats}
                        loading={loading}
                        headerHeight={70}
                    >
                        {duosTableHeaders.map((header, index) => (
                            <Column key={index} width={187}>
                                <HeaderCell>{header.headerName}</HeaderCell>
                                <NumberCell className={header.headerName} rowData={duosStats} dataKey={header.field} />
                            </Column>
                        ))}
                    </Table>
                </div>

                <div className="export">
                    {/* <Tab 
                        tabNames={[ "capacity" ]}
                        activeTab={activeTab}
                        onClick={(n, i) => {
                            setActiveTab(n);
                            setImmutable(true);
                        }}
                    /> */}
                    <div></div>
                    <div className="toggles">
                        <div 
                            className="option-title" 
                            onClick={() => {
                                setOpenDd(!openDd);
                                setImmutable(false);
                            }}
                        >
                            <span>Annotations</span>
                            <FontAwesomeIcon icon={faEllipsisV} className="dd-icon" />
                        </div>
                        {openDd && (
                            <div ref={ddRef} className={clsx("options")}>
                                {_.uniqBy([...plotLines, ...plotBands], (item) => item.label).map((line, index) => (
                                    <Checkbox 
                                        key={index}
                                        text={line.label}
                                        isActive={lines.includes(line.label)}
                                        toggleCheckbox={() => toggleLines(line.label)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="chart">
                    <Loader 
                        type={LoaderTypes.Big}
                        isLoading={loading}
                        loadingText="loading"
                    />
                    {tooltipText && ( <div className="tooltip" style={{ color: tooltipColor }}>{tooltipText}</div> )}
                    <HighchartsReact 
                        highcharts={Highcharts} 
                        options={options}
                        oneToOne
                        allowChartUpdate
                        immutable={immutable}
                    />
                </div>

                <div className="tcr">
                    <span className="message">
                        <p>After <a href="https://www.ofgem.gov.uk/electricity/transmission-networks/charging/targeted-charging-review-significant-code-review" target="_blank" rel="noreferrer">the TCR</a> takes effect next April (2022), your meter will fall under <span>Residual Band {capacity?.band}</span>. The new TCR bands are based on your meter's import capacity.</p>
                        <p>This analysis only considers the effect on your meter's DUoS charges. The TCR will also change the way that TNUoS charges are levied on customers but this change has been delayed until April 2023. The new TNUoS charges will also be based on the same residual bands, but full details are yet to be finalised.</p>
                        <p>See a breakdown of the bands here:</p>
                        <p>To find out more contact us at <a href="mailto:enquiry@ngpltd.co.uk">enquiry@ngpltd.co.uk</a></p>
                    </span>
                    <div className="bands-table">
                        <span className="table-title">Residual TCR Bands</span>
                        <Table
                            height={225}
                            bordered
                            cellBordered
                            loading={loading}
                            data={bandsTableData}
                        >
                            {bandHeaders.map((header, index) => (
                                <Column key={index} flexGrow={1}>
                                    <HeaderCell>{header.headerName}</HeaderCell>
                                    <NumberCell className="" rowData={stats} dataKey={header.field} />
                                </Column>
                            ))}
                        </Table>
                    </div>
                </div>
            </div>
        </PDFExport>
    )
}



export default Capacity;